import { HttpClient } from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { InteractionStatus } from '@azure/msal-browser';
import { app, authentication } from '@microsoft/teams-js';
import moment, { Moment } from 'moment';
import { duration } from 'moment/moment';
import { EMPTY, from, Observable, of, ReplaySubject, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, map, mapTo, skipWhile, switchMap, take, tap, timeout } from 'rxjs/operators';
import { FetchStrategy, RecurringTaskRunnerFactory } from '@nimbus-library/features/recurring-task';
import { LogoutRequest } from '@nimbus-library/features/authentication/models/authentication-request.model';
import { MicrosoftResource } from '@nimbus-library/features/authentication/models/graph-permission.enum';
import { IAuthenticationStorage } from '@nimbus-library/features/authentication/services/i-authentication-storage';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { IUserInfo } from '@nimbus-library/features/menu/models/i-user.info';
import { IMicrosoftTeamsContextAccessor } from '@nimbus-library/features/microsoft-teams/contract/i-microsoft-teams-context.accessor';

export interface AccessTokenResponse {
  accessToken: string;
  expiresOn: Moment;
}

@Injectable()
export class TeamsAuthenticationService extends IAuthenticationService {
  private readonly TEAMS_CONTEXT_TIMEOUT = 500;
  private readonly TEAMS_TOKEN_TIMEOUT = 1000;
  public status$: Observable<InteractionStatus> = of(null);
  private userInfo: IUserInfo;
  private accessToken$: ReplaySubject<AccessTokenResponse> = new ReplaySubject(1);

  public get isAuthenticated(): boolean {
    return this.isAuthenticated$.value;
  }

  public constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    @Optional() private readonly teamsContextAccessor: IMicrosoftTeamsContextAccessor,
    recurringTaskRunnerFactory: RecurringTaskRunnerFactory,
    authenticationStorage: IAuthenticationStorage,
  ) {
    super(recurringTaskRunnerFactory, authenticationStorage);

    this.isAuthenticated$
      .pipe(
        skipWhile((isAuthenticated) => isAuthenticated === undefined),
        distinctUntilChanged(),
        switchMap((isAuthenticated) => {
          if (!isAuthenticated) {
            this.authenticationStorage.clearAuthData();
            return EMPTY;
          }
          return this.getAccessTokenRenewalTimer();
        }),
      )
      .subscribe();
  }

  public getApiToken$(forceRefresh: boolean, scopes: string[]) {
    return this.getAccessToken$(forceRefresh, scopes).pipe(
      map((result) => this.processToken(result.accessToken, MicrosoftResource.GraphApi)),
    );
  }

  public getRawApiToken$(forceRefresh: boolean, scopes: string[]) {
    return this.getAccessToken$(forceRefresh, scopes).pipe(map((result) => result.accessToken));
  }

  public getUserInfo(): IUserInfo {
    return this.userInfo;
  }

  public grantExplicitConsent$(scopes: string[], redirectUrl: string) {}

  public logout(destroyServerSession: boolean, logoutRequest: LogoutRequest, reload: boolean = true) {
    this.isAuthenticated$.next(false);
    this.authenticationStorage.clearAuthData();
    this.logout$.next();
    this.router.navigate(['/', 'authentication', 'login'], {
      queryParams: {
        ...logoutRequest,
        autoLogin: false,
      },
      queryParamsHandling: 'merge',
    });
  }

  public trackAuthentication(): Observable<void> {
    return of(undefined);
  }

  public login() {
    return this.aquireIdToken$().pipe(
      take(1),
      tap(() => {
        this.isAuthenticated$.next(true);
      }),
      catchError((err) => {
        console.error(err);
        this.isAuthenticated$.next(false);

        return throwError(err);
      }),
      mapTo(undefined),
    );
  }

  public initialize$(): Observable<boolean> {
    return (app.isInitialized() ? of(true) : from(app.initialize()).pipe(timeout(this.TEAMS_CONTEXT_TIMEOUT), mapTo(true))).pipe(
      mapTo(true),
    );
  }

  private getAccessToken$(forceRefresh: boolean, scopes: string[]) {
    return this.accessToken$.pipe(take(1));
  }

  private refreshExchangeToken(scopes: string[]): Observable<AccessTokenResponse> {
    return this.aquireIdToken$().pipe(
      switchMap((tokenResponse) => {
        const userInfo = this.getUserInfo();
        return this.exchangeToken(userInfo.tenantId, tokenResponse.token, scopes);
      }),
    );
  }

  private exchangeToken(tenantId: string, idToken: string, scopes?: string[]): Observable<AccessTokenResponse> {
    return this.http
      .post<{ accessToken: string; expiresOn: string }>(`api/permissionmanager/v1/token/${tenantId}`, {
        scopes: scopes || this.defaultScopes,
        idToken,
      })
      .pipe(map((response) => ({ accessToken: response.accessToken, expiresOn: moment(response.expiresOn) })));
  }

  public aquireIdToken$(): Observable<{ token: string; expiration: moment.Moment }> {
    return this.initialize$().pipe(
      switchMap(() => from(authentication.getAuthToken())),
      timeout(this.TEAMS_TOKEN_TIMEOUT),
      map((token) => {
        const decodedToken = this.decodeToken(token);

        this.userInfo = {
          name: decodedToken.name,
          tenantId: decodedToken.tid,
          userId: decodedToken.oid,
          userName: decodedToken.preferred_username,
        };

        this.teamsContextAccessor?.trySpecifyUser({
          loginHint: this.userInfo.userName,
          tenantId: this.userInfo.tenantId,
          userId: this.userInfo.userId,
        });

        const result = { token, expiration: moment(decodedToken.exp * 1000) };
        this.authenticationStorage.saveAuthData(result.token, this.getUserInfo(), result.expiration);
        this.authenticationStorage.saveTeamsInfo(decodedToken.preferred_username, result.expiration);
        return result;
      }),
      tap(() => this.isAuthenticated$.next(true)),
      catchError((err) => {
        console.error(err);
        return throwError(err);
      }),
    );
  }

  public sso(loginHint: string): Observable<boolean> {
    return this.aquireIdToken$().pipe(
      take(1),
      tap(() => {
        this.isAuthenticated$.next(true);
      }),
      catchError((err) => {
        this.isAuthenticated$.next(false);
        console.error(err);
        return of(false);
      }),
      map(() => this.isAuthenticated$.value),
    );
  }

  private getAccessTokenRenewalTimer() {
    return this.recurringTaskRunnerFactory
      .createRunner(
        () =>
          this.refreshExchangeToken([]).pipe(
            take(1),
            tap((response) => {
              this.accessToken$.next(response);
            }),
          ),
        { strategy: FetchStrategy.BackgroundFetch },
      )
      .start(undefined, duration('10', 'minutes'));
  }
}
